/**
 * Morse als Bild — Punkt und Strich zum Ansehen, Silben zum Mitsprechen. (06.10.2026, Morse-Tabelle)
 *
 * Martin: „Was mir fehlt, ist beim Morse-Code auch eine Tabelle. So ist es mir noch viel zu schwer.“
 * Vorher standen Muster nur als Schrift da („−·−“) — klein, und Punkt und Strich unterschieden sich kaum.
 *
 * Jetzt ist das Muster ein BILD der Töne: der Punkt ein kleiner Kreis (eine Einheit), der Strich ein Balken drei
 * Einheiten lang, dazwischen eine Einheit Luft — genau das Verhältnis, in dem sie klingen. Der Unterschied ist eine
 * FORM (Kreis gegen Balken), keine Farbe. Die Größe kommt aus style.css (`.morse-bild[data-groesse]`).
 *
 * `leuchte` lässt das Bild mit dem Ton mitlaufen: jedes Element wird hell, solange sein Ton klingt — so sieht man,
 * was man hört. Über die Web Animations API, also ohne `style`-Attribut; bei reduzierter Bewegung nicht.
 */
import { musterSilben, type ZeitPlan } from "../../shared/morse.js";

/** Punkte und Striche zum Ansehen als Schrift: · und −. */
export function musterZeigen(muster: string): string {
  return [...muster].map((x) => (x === "." ? "·" : "−")).join("");
}

/** Punkte und Striche zum Vorlesen (Bildschirmleser): „kurz lang“. */
export function musterSprechen(muster: string): string {
  return [...muster].map((x) => (x === "." ? "kurz" : "lang")).join(" ");
}

/** Bewegung reduziert? (Dann leuchtet nichts mit, und die Frist-Leiste springt in Stufen.) */
export function wenigBewegung(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

type BildGroesse = "s" | "m" | "l";

/** Ein Muster als Bild: Punkt = Kreis, Strich = Balken dreimal so lang. Für Bildschirmleser „kurz lang …“. */
export function musterBild(muster: string, groesse: BildGroesse = "m"): HTMLElement {
  const b = document.createElement("span");
  b.className = "morse-bild";
  b.dataset.groesse = groesse;
  b.setAttribute("role", "img");
  b.setAttribute("aria-label", musterSprechen(muster) || "kein Muster");
  for (const x of muster) {
    if (x !== "." && x !== "-") continue;
    const e = document.createElement("span");
    e.className = x === "." ? "morse-bild-punkt" : "morse-bild-strich";
    b.appendChild(e);
  }
  return b;
}

/** Die Silben eines Musters als Element („dah-di-dah“) — nur zum Lesen, das Bild daneben trägt den Namen. */
export function silbenText(muster: string, klasse = "morse-silben"): HTMLElement {
  const s = document.createElement("span");
  s.className = klasse;
  s.textContent = musterSilben(muster);
  return s;
}

/**
 * Das Bild leuchtet mit dem Ton: Element für Element, solange sein Ton klingt. `inMs`: wann der erste Ton
 * beginnt. Gibt den Halt zurück (mehrfach rufbar) — beim Abbruch des Tons endet auch das Leuchten.
 * Passt das Bild nicht zum Plan (andere Zahl Elemente), bleibt es still.
 */
export function leuchte(bild: HTMLElement, plan: ZeitPlan, inMs: number): () => void {
  const teile = [...bild.children] as HTMLElement[];
  if (wenigBewegung() || teile.length !== plan.toene.length || teile.length === 0 || typeof teile[0].animate !== "function") return () => undefined;
  bild.dataset.spielt = "true";
  const an = plan.toene.map((t, i) =>
    teile[i].animate([{ opacity: 1 }, { opacity: 1 }], { delay: Math.max(0, inMs + t.beginnMs), duration: Math.max(1, t.dauerMs), fill: "none" }),
  );
  let aus = false;
  const halt = (): void => {
    if (aus) return;
    aus = true;
    clearTimeout(uhr);
    for (const a of an) {
      try {
        a.cancel();
      } catch {
        /* egal */
      }
    }
    delete bild.dataset.spielt;
  };
  const uhr = setTimeout(halt, inMs + plan.gesamtMs + 150);
  return halt;
}
